/**
 * Design-QA route for the grammar-tip videos (2026-10-08). No Supabase session needed:
 *
 *   /grammar-video-review               — every grammar card that has a video, unit by unit (links)
 *   /grammar-video-review?lesson=<id>   — that card inside the lesson screen; ▶ „ნახე ვიდეო“ opens the player
 *
 * Tornike picked the button look over a video-picture card (look 2, removed).
 */
import { useTranslation } from 'react-i18next';
import { Button, Container, Link, ProgressBar, Screen, Stack, SubmitBar } from '@fluent/ui';
import type { GrammarTip as GrammarTipExercise, Lesson } from '@fluent/types';
import { GrammarTip } from './lesson/GrammarTip.js';
import { getLesson } from './content/lessons.js';
import { getCourseTree } from './content/course.js';
import './lesson/LessonPlayer.css';

const tipOf = (lesson: Lesson | null) =>
  lesson?.exercises.find((e): e is GrammarTipExercise => e.type === 'grammar_tip');

export function GrammarVideoReview() {
  const id = new URLSearchParams(window.location.search).get('lesson');
  return id ? <CardView lessonId={id} /> : <CardList />;
}

/** Every card with a video, as "Unit U · lesson N — title" links (N = play position inside the unit). */
function CardList() {
  const tree = getCourseTree();
  if (!tree) return <div>No course tree.</div>;
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-6, 24px) 0' }}>
          {tree.units.map((unit, u) => {
            const rows = unit.resolvedSkills
              .flatMap((s) => s.resolvedLessons)
              .map((lesson, n) => ({ lesson, n: n + 1, tip: tipOf(lesson) }))
              .filter((r) => r.tip?.video_key);
            if (!rows.length) return null;
            return (
              <Stack key={unit.id} gap={2}>
                <strong>Unit {u + 1}</strong>
                {rows.map((r) => (
                  <Link key={r.lesson.id} href={`/grammar-video-review?lesson=${r.lesson.id}`}>
                    {`lesson ${r.n} — ${r.tip?.title_ka ?? r.lesson.id}`}
                  </Link>
                ))}
              </Stack>
            );
          })}
        </Stack>
      </Container>
    </Screen>
  );
}

function CardView({ lessonId }: { lessonId: string }) {
  const { t } = useTranslation();
  const lesson = getLesson(lessonId);
  const tip = tipOf(lesson);
  if (!lesson || !tip) return <div>No grammar tip in {lessonId}.</div>;
  const noop = () => undefined;

  return (
    <Screen>
      <header className="fluent-lesson__topbar">
        <Button variant="ghost" size="sm" aria-label={t('lesson.exit_aria')}>
          ✕
        </Button>
        <Button variant="ghost" size="sm" disabled aria-label={t('lesson.back_aria')}>
          ←
        </Button>
        <ProgressBar value={0} tone="primary" size="md" label={`1 / ${lesson.exercises.length}`} />
        <Button variant="neutral" size="sm" className="fluent-lesson__report-flag">
          {t('lesson.report_error_label')}
        </Button>
      </header>
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-6, 24px) 0 140px' }}>
          <div className="fluent-lesson__exercise-slot">
            <GrammarTip exercise={tip} locked={false} />
          </div>
        </Stack>
      </Container>
      <div className="fluent-lesson__dock">
        <SubmitBar ctaLabel={t('lesson.intro_continue')} onCta={noop} />
      </div>
    </Screen>
  );
}
